import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { ChevronRight, X } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { ProductCard } from "../product/productCard";
import { adaptProductForCard } from "../utils";
import { fetchProducts } from "../../redux/reducers/productSlice";
import { addToCartAsync } from "../../redux/reducers/cart";
import "./bestSeller.css";
import { Link } from "react-router-dom";

/* ── Mobile Size Picker Bottom-Sheet ── */
function SizePickerSheet({ product, onClose }) {
  const dispatch = useDispatch();
  const [pickedSize, setPickedSize] = useState(null);

  if (!product) return null;

  const {
    id,
    title,
    slug,
    brand,
    price,
    originalPrice,
    currency,
    image,
    variant,
  } = product;

  const sizes = variant?.sizes || [];
  const inStock = sizes.filter((s) => s.stock > 0);
  const outOfStock = sizes.filter((s) => s.stock <= 0);

  const handleAdd = () => {
    if (!pickedSize) return;
    dispatch(
      addToCartAsync({
        productId: id,
        name: title,
        slug,
        brand,
        basePrice: price,
        originalPrice,
        currency,
        image,
        color: variant.color,
        size: pickedSize.size,
        sku: pickedSize.sku,
        stock: pickedSize.stock,
        quantity: 1,
      }),
    );
    onClose();
  };

  return (
    <div className="size-sheet-overlay" onClick={onClose}>
      <div className="size-sheet" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="size-sheet-header">
          <img
            className="size-sheet-img"
            src={image}
            alt={title}
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
          <div className="size-sheet-meta">
            <p className="size-sheet-title">{title}</p>
            <p className="size-sheet-price">
              {currency?.toLowerCase() || "kes"} {price}
            </p>
          </div>
          <button
            className="size-sheet-close"
            onClick={onClose}
            aria-label="Close size picker"
            type="button"
          >
            <X size={20} strokeWidth={1.5} />
          </button>
        </div>

        {/* Color */}
        <p className="size-sheet-label">Color: {variant?.color?.name || "—"}</p>

        {/* Sizes */}
        <p className="size-sheet-label">Select Size</p>
        <div className="size-sheet-grid">
          {inStock.map((s) => {
            const isSel = pickedSize?.size === s.size;
            return (
              <button
                key={s.size}
                type="button"
                className={`size-sheet-btn ${isSel ? "size-sheet-btn--selected" : ""}`}
                onClick={() => setPickedSize(s)}
              >
                {s.size}
              </button>
            );
          })}
          {outOfStock.map((s) => (
            <button
              key={s.size}
              type="button"
              className="size-sheet-btn size-sheet-btn--disabled"
              disabled
            >
              {s.size}
            </button>
          ))}
        </div>

        {/* Add action */}
        <div className="size-sheet-action">
          <button
            className="size-sheet-add-btn"
            disabled={!pickedSize}
            onClick={handleAdd}
            type="button"
          >
            Add to bag
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Product Grid ── */
export default function ProductGrid({ division }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [pageCount, setPageCount] = useState(1);
  const [sheetProduct, setSheetProduct] = useState(null);
  const { wishlist } = useSelector((state) => state.wishlist);
  const [fetched, setFetched] = useState(false);
  const scrollRef = useRef(null);
  const dispatch = useDispatch();

  const { products, loadingList, error } = useSelector(
    (state) => state.products,
  );

  useEffect(() => {
    dispatch(fetchProducts({ page: 1, limit: 20 })).finally(() =>
      setFetched(true),
    );
  }, [dispatch]);

  /* ── Filter by division, sort by `sold` descending (most sold first) ── */
  const visibleProducts = useMemo(() => {
    if (!products) return [];
    return products
      .filter((p) => p.sex?.toLowerCase() === division?.toLowerCase())
      .sort((a, b) => (b.sold ?? 0) - (a.sold ?? 0));
  }, [products, division]);

  const updatePagination = useCallback(() => {
    if (!scrollRef.current) return;
    const { scrollWidth, clientWidth } = scrollRef.current;
    if (clientWidth <= 0) return;
    const totalPages = Math.max(1, Math.ceil(scrollWidth / clientWidth));
    setPageCount(totalPages);
  }, []);

  useEffect(() => {
    if (!scrollRef.current) return;
    const resizeObserver = new ResizeObserver(() => {
      updatePagination();
    });
    resizeObserver.observe(scrollRef.current);
    return () => resizeObserver.disconnect();
  }, [updatePagination, visibleProducts]);

  const handleScroll = () => {
    if (!scrollRef.current || pageCount <= 1) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    const maxScroll = scrollWidth - clientWidth;
    if (maxScroll <= 0) return;
    const scrollPercentage = Math.min(Math.max(scrollLeft / maxScroll, 0), 1);
    const newIndex = Math.round(scrollPercentage * (pageCount - 1));
    if (newIndex !== activeIndex) {
      setActiveIndex(newIndex);
    }
  };

  const scrollToIndex = (index) => {
    if (!scrollRef.current) return;
    const { scrollWidth, clientWidth } = scrollRef.current;
    const maxScroll = scrollWidth - clientWidth;
    const targetScrollLeft = (index / (pageCount - 1)) * maxScroll;
    scrollRef.current.scrollTo({
      left: targetScrollLeft,
      behavior: "smooth",
    });
  };

  if (visibleProducts.length === 0 && (loadingList || !fetched)) {
    return (
      <section className="pg-wrap pg-wrap--skeleton" aria-busy="true">
        <header className="pg-header">
          <div className="pg-header-text">
            <p className="pg-subheading">&nbsp;</p>
            <h2 className="pg-heading">&nbsp;</h2>
          </div>
        </header>
        <div className="pg-scroll">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="pc-card" aria-hidden="true">
              <div className="pc-image-wrap pc-image-loading">
                <div className="pc-skeleton">
                  <div className="pc-skeleton-shimmer" />
                </div>
              </div>
              <div className="pc-details" style={{ minHeight: 110 }} />
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <div className="pg-error" role="alert">
        <p>Unable to load products. {error}</p>
      </div>
    );
  }

  return (
    <>
      <section className="pg-wrap" aria-label="Best Sellers">
        <header className="pg-header">
          <div className="pg-header-text">
            <p className="pg-subheading">Shop our most popular items</p>
            <h2 className="pg-heading">
              Most Loved {division ? `by ${division}` : ""}
            </h2>
          </div>
          <Link
            to="/all-products"
            state={{ tag: "bestseller", division }}
            className="pg-viewall"
          >
            <span>View all</span>
            <ChevronRight size={16} strokeWidth={2.2} />
          </Link>
        </header>

        <div
          className="pg-scroll"
          ref={scrollRef}
          onScroll={handleScroll}
          tabIndex={0}
          aria-label="Product horizontal list"
        >
          {visibleProducts.map((p, i) => {
            const adapted = adaptProductForCard(p);
            return (
              <ProductCard
                key={adapted.id}
                {...adapted}
                priority={i < 2}
                onPickSize={setSheetProduct}
              />
            );
          })}
        </div>

        {pageCount > 1 && (
          <nav className="pg-dots" aria-label="Carousel pagination">
            {Array.from({ length: pageCount }).map((_, index) => (
              <button
                key={index}
                type="button"
                className={`pg-dot ${index === activeIndex ? "pg-dot--active" : ""}`}
                onClick={() => scrollToIndex(index)}
                aria-label={`Go to slide ${index + 1} of ${pageCount}`}
                aria-current={index === activeIndex ? "true" : "false"}
              />
            ))}
          </nav>
        )}
      </section>

      {/* Bottom sheet rendered at root level so it isn't clipped by the scroll container */}
      {sheetProduct && (
        <SizePickerSheet
          product={sheetProduct}
          onClose={() => setSheetProduct(null)}
        />
      )}
    </>
  );
}
